/**
 * Broker Dedup Workspace — manager-facing page for the one-time and
 * ongoing cleanup of duplicate brokers, agents, and lead conflicts.
 *
 * Four tabs:
 *   Clusters    — duplicate-broker groups; pick master, merge or mark distinct
 *   Conflicts   — pending lead conflicts awaiting a manager decision
 *   History     — past merges (with restore button within the retention window)
 *   Settings    — admin config (thresholds, lookback, conflict rule)
 *
 * The page is admin/HOD/Indirect Sales Lead only. We render a 403
 * fallback for non-admin users instead of hiding the route, so a
 * shared-link click from a sales agent still produces a useful
 * message.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Layers, AlertOctagon, History as HistoryIcon, Settings as SettingsIcon,
  CheckCircle2, XCircle, ArrowRightLeft, Building2, RefreshCw,
  ShieldCheck, Undo2, AlertTriangle, Users, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCORE_TONE = (score) => {
  if (score >= 95) return 'bg-red-100 text-red-700 border-red-300';
  if (score >= 85) return 'bg-amber-100 text-amber-700 border-amber-300';
  return 'bg-slate-100 text-slate-700 border-slate-300';
};

// ---- Page-level admin gate

const isAdminRole = (role) => {
  const r = (role || '').toLowerCase();
  return ['admin', 'owner', 'ceo', 'hod', 'super_admin', 'indirect_sales_lead']
    .some((h) => r.includes(h));
};

export default function BrokerDedupWorkspace() {
  const { token, user } = useAuth();
  const [tab, setTab] = useState('clusters');
  const [digest, setDigest] = useState(null);
  const isAdmin = isAdminRole(user?.role);

  const fetchDigest = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/broker-dedup/digest?token=${token}`);
      setDigest(res.data?.data);
    } catch (e) {
      // Silent — digest is decorative, not blocking.
    }
  }, [token]);

  useEffect(() => { fetchDigest(); }, [fetchDigest]);

  if (!isAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle size={20} /> Manager access required
            </CardTitle>
            <CardDescription>
              The Broker Dedup Workspace is only available to admins,
              HODs, and the Indirect Sales lead. Ask your manager to
              run cleanups on your behalf.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <div className="max-w-7xl mx-auto p-6 space-y-5">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Layers size={26} className="text-violet-500" />
            Broker Dedup Workspace
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Resolve duplicate broker companies, broker agents, and contested client registrations.
          </p>
        </div>

        {digest && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <DigestTile label="Clusters open" value={digest.clusters_unresolved} icon={Layers} tone="violet" />
            <DigestTile label="Conflicts pending" value={digest.conflicts_pending} icon={AlertOctagon} tone="red" />
            <DigestTile label="Resolved this week" value={digest.conflicts_resolved_this_week} icon={CheckCircle2} tone="emerald" />
            <DigestTile label="Merges this week" value={digest.merges_this_week} icon={ArrowRightLeft} tone="blue" />
            <DigestTile label="Overrides this week" value={digest.overrides_count} icon={ShieldCheck} tone="amber" />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="clusters">
              <Layers size={14} className="mr-1.5" /> Clusters
            </TabsTrigger>
            <TabsTrigger value="conflicts">
              <AlertOctagon size={14} className="mr-1.5" /> Conflicts
            </TabsTrigger>
            <TabsTrigger value="history">
              <HistoryIcon size={14} className="mr-1.5" /> History
            </TabsTrigger>
            <TabsTrigger value="settings">
              <SettingsIcon size={14} className="mr-1.5" /> Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="clusters" className="space-y-2">
            <ClustersTab token={token} onChanged={fetchDigest} />
          </TabsContent>
          <TabsContent value="conflicts" className="space-y-2">
            <ConflictsTab token={token} onChanged={fetchDigest} />
          </TabsContent>
          <TabsContent value="history" className="space-y-2">
            <HistoryTab token={token} onChanged={fetchDigest} />
          </TabsContent>
          <TabsContent value="settings" className="space-y-2">
            <SettingsTab token={token} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const TONE = {
  violet: 'border-violet-200 bg-violet-50 text-violet-800',
  red: 'border-red-200 bg-red-50 text-red-800',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  blue: 'border-blue-200 bg-blue-50 text-blue-800',
  amber: 'border-amber-200 bg-amber-50 text-amber-800',
};
const DigestTile = ({ label, value, icon: Icon, tone }) => (
  <div className={`rounded-md border p-3 ${TONE[tone] || TONE.blue}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase font-medium flex items-center gap-1">
        <Icon size={12} /> {label}
      </span>
    </div>
    <div className="text-2xl font-bold mt-1">{value ?? '—'}</div>
  </div>
);

// ---- Clusters tab

const ClustersTab = ({ token, onChanged }) => {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mergeTarget, setMergeTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/broker-dedup/clusters?token=${token}`);
      setClusters(res.data?.data?.clusters || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load clusters');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const markDistinct = async (cluster) => {
    const note = window.prompt('Why are these genuinely different brokers? (optional)') || '';
    try {
      await axios.post(`${API}/broker-dedup/mark-distinct?token=${token}`, {
        broker_ids: cluster.members.map((m) => m.id),
        note,
      });
      toast.success('Marked as distinct — won\'t reappear in clusters');
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark distinct');
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">Suspected duplicate clusters</CardTitle>
          <CardDescription>
            Each row groups 2+ brokers that fuzzy-match. Pick a master to keep, then merge.
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw size={13} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && !clusters.length ? (
          <Skeleton className="h-24 w-full" />
        ) : clusters.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            <CheckCircle2 size={32} className="mx-auto text-emerald-400 mb-2" />
            No duplicate clusters detected. Your broker list is clean.
          </div>
        ) : (
          clusters.map((c) => (
            <ClusterRow
              key={c.cluster_id}
              cluster={c}
              onMerge={() => setMergeTarget(c)}
              onMarkDistinct={() => markDistinct(c)}
            />
          ))
        )}
      </CardContent>

      <MergeModal
        cluster={mergeTarget}
        token={token}
        onClose={() => setMergeTarget(null)}
        onCompleted={() => { setMergeTarget(null); load(); onChanged?.(); }}
      />
    </Card>
  );
};

const ClusterRow = ({ cluster, onMerge, onMarkDistinct }) => (
  <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3">
    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
      <div className="flex items-center gap-2">
        <Building2 size={16} className="text-violet-500" />
        <span className="font-semibold">{cluster.members[0].name || cluster.members[0].company}</span>
        <Badge className={`text-[10px] border ${SCORE_TONE(cluster.best_score)}`}>
          {cluster.best_score}% similarity
        </Badge>
        <Badge variant="outline" className="text-[10px]">
          {cluster.size} variants
        </Badge>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={onMarkDistinct} className="h-8">
          <ShieldCheck size={13} className="mr-1.5" /> Mark distinct
        </Button>
        <Button size="sm" className="h-8 bg-violet-600 hover:bg-violet-500 text-white" onClick={onMerge}>
          <ArrowRightLeft size={13} className="mr-1.5" /> Merge…
        </Button>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
      {cluster.members.map((m) => (
        <div
          key={m.id}
          className={`rounded border p-2 text-xs ${
            m.id === cluster.suggested_master_id
              ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20'
              : 'border-slate-200'
          }`}
        >
          <div className="flex items-center justify-between gap-1">
            <span className="font-semibold truncate">{m.name || m.company || '—'}</span>
            {m.id === cluster.suggested_master_id && (
              <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-700">
                Suggested master
              </Badge>
            )}
          </div>
          <div className="text-slate-500 mt-0.5 space-y-0.5">
            {m.email && <div>{m.email}</div>}
            {m.phone && <div>{m.phone}</div>}
            {m.rera_number && <div>RERA: {m.rera_number}</div>}
            <div className="text-slate-400">
              {m.total_sales || 0} sales · {m.total_agents || 0} agents
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const MergeModal = ({ cluster, token, onClose, onCompleted }) => {
  const [masterId, setMasterId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // Per-field "which variant wins" for each editable column. Default
  // to the suggested master's value, but the user can flip any field
  // to a sibling variant before merging.
  const [keepFields, setKeepFields] = useState({});

  useEffect(() => {
    if (!cluster) { setMasterId(''); setKeepFields({}); setReason(''); return; }
    setMasterId(cluster.suggested_master_id);
    const m = cluster.members.find((x) => x.id === cluster.suggested_master_id);
    setKeepFields({
      name: m?.name || '',
      company: m?.company || '',
      email: m?.email || '',
      phone: m?.phone || '',
      rera_number: m?.rera_number || '',
      trade_license: m?.trade_license || '',
    });
  }, [cluster]);

  const submit = async () => {
    if (!cluster) return;
    setBusy(true);
    try {
      const dupes = cluster.members
        .map((m) => m.id)
        .filter((id) => id !== masterId);
      await axios.post(`${API}/broker-dedup/merge?token=${token}`, {
        master_id: masterId,
        duplicate_ids: dupes,
        keep_fields: keepFields,
        reason,
      });
      toast.success(`Merged ${dupes.length} broker(s) into master`);
      onCompleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally {
      setBusy(false);
    }
  };

  if (!cluster) return null;

  return (
    <Dialog open={!!cluster} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Merge {cluster.size} broker variants</DialogTitle>
          <DialogDescription>
            Pick the master record to keep, then choose which field values land on it.
            All agents and sales from the others will be reassigned. Duplicates are soft-deleted
            (recoverable for 30 days).
          </DialogDescription>
        </DialogHeader>

        <div>
          <Label className="text-xs text-slate-600">Master record</Label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
            {cluster.members.map((m) => (
              <label
                key={m.id}
                className={`border rounded p-2 cursor-pointer text-xs ${
                  masterId === m.id
                    ? 'border-violet-400 bg-violet-50 dark:bg-violet-950/20'
                    : 'border-slate-200'
                }`}
              >
                <input
                  type="radio"
                  className="mr-2"
                  checked={masterId === m.id}
                  onChange={() => {
                    setMasterId(m.id);
                    // When the user picks a new master, refresh the
                    // keepFields defaults to that master's values — they
                    // can still flip individual fields below.
                    setKeepFields({
                      name: m.name || '',
                      company: m.company || '',
                      email: m.email || '',
                      phone: m.phone || '',
                      rera_number: m.rera_number || '',
                      trade_license: m.trade_license || '',
                    });
                  }}
                />
                <span className="font-semibold">{m.name || m.company}</span>
                <div className="text-slate-500 mt-0.5">
                  {m.email || '—'} · {m.phone || '—'} · RERA {m.rera_number || '—'}
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs text-slate-600">Fields to keep on master (edit if needed)</Label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {['name', 'company', 'email', 'phone', 'rera_number', 'trade_license'].map((f) => (
              <div key={f}>
                <label className="text-[10px] uppercase text-slate-500">{f.replace('_', ' ')}</label>
                <Input
                  value={keepFields[f] || ''}
                  onChange={(e) => setKeepFields({ ...keepFields, [f]: e.target.value })}
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-xs text-slate-600">Merge reason / note (optional)</Label>
          <Textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. confirmed via RERA database, same trade license"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            className="bg-violet-600 hover:bg-violet-500 text-white"
            onClick={submit}
            disabled={busy || !masterId}
          >
            {busy ? <><RefreshCw size={13} className="mr-1.5 animate-spin" /> Merging…</>
                  : <><ArrowRightLeft size={13} className="mr-1.5" /> Merge into master</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Conflicts tab

const ConflictsTab = ({ token, onChanged }) => {
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/broker-dedup/conflicts`, {
        params: { token, status: filter !== 'all' ? filter : undefined },
      });
      setConflicts(res.data?.data?.conflicts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load conflicts');
    } finally { setLoading(false); }
  }, [token, filter]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (conflictId, winner) => {
    const reason = window.prompt(
      `Awarding to the ${winner} broker. Add a reason (optional):`,
    );
    if (reason === null) return;
    try {
      await axios.post(`${API}/broker-dedup/conflicts/${conflictId}/resolve?token=${token}`, {
        winner, reason,
      });
      toast.success('Conflict resolved');
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not resolve');
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base">Lead conflicts</CardTitle>
          <CardDescription>
            When two brokers register the same client, the manager decides who owns the lead.
          </CardDescription>
        </div>
        <div className="flex items-center gap-2">
          {['pending', 'resolved', 'all'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className="h-7 text-xs capitalize"
            >
              {f}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && !conflicts.length ? (
          <Skeleton className="h-24 w-full" />
        ) : conflicts.length === 0 ? (
          <div className="py-12 text-center text-slate-500">
            <CheckCircle2 size={32} className="mx-auto text-emerald-400 mb-2" />
            No {filter !== 'all' ? filter : ''} conflicts.
          </div>
        ) : (
          conflicts.map((c) => (
            <ConflictRow key={c.id} conflict={c} onResolve={(w) => resolve(c.id, w)} />
          ))
        )}
      </CardContent>
    </Card>
  );
};

const ConflictRow = ({ conflict, onResolve }) => {
  const isPending = conflict.status === 'pending';
  const newPayload = conflict.new_sale_payload || {};
  const existing = conflict.existing_sale_snapshot || {};
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-md p-3">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <AlertOctagon size={16} className="text-red-500" />
          <span className="font-semibold">
            Client: {newPayload.buyer_name || existing.buyer_name || 'Unknown'}
          </span>
          <Badge className={`text-[10px] border ${
            isPending ? 'border-amber-300 text-amber-700 bg-amber-50' :
            'border-emerald-300 text-emerald-700 bg-emerald-50'
          }`}>
            {conflict.status}
          </Badge>
          <span className="text-xs text-slate-500">
            opened {conflict.created_at && new Date(conflict.created_at).toLocaleDateString()}
          </span>
        </div>
        {isPending && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              onClick={() => onResolve('existing')}
            >
              <CheckCircle2 size={13} className="mr-1.5" /> Award existing
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={() => onResolve('new')}
            >
              <CheckCircle2 size={13} className="mr-1.5" /> Award new
            </Button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
        <div className="rounded border border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/10 p-2">
          <div className="font-semibold mb-1">Existing registration</div>
          <div>Broker: {conflict.existing_broker_name || '—'}</div>
          <div>Agent: {existing.agent_name || '—'}</div>
          <div>Property: {existing.property_name || '—'}{existing.unit_number ? ` · ${existing.unit_number}` : ''}</div>
          <div>Date: {existing.sale_date || '—'}</div>
          <div>Status: {existing.payment_status || '—'}</div>
        </div>
        <div className="rounded border border-blue-200 bg-blue-50/50 dark:bg-blue-950/10 p-2">
          <div className="font-semibold mb-1">New registration (parked)</div>
          <div>Broker: {conflict.new_broker_name || newPayload.broker_name || '—'}</div>
          <div>Agent: {newPayload.agent_name || '—'}</div>
          <div>Property: {newPayload.property_name || '—'}{newPayload.unit_number ? ` · ${newPayload.unit_number}` : ''}</div>
          <div>Date: {newPayload.sale_date || '—'}</div>
        </div>
      </div>
      {conflict.decision_reason && (
        <p className="text-xs text-slate-600 mt-2">
          <strong>Decision:</strong> {conflict.decision_reason}
          {conflict.decided_by_name && <span> — by {conflict.decided_by_name}</span>}
        </p>
      )}
    </div>
  );
};

// ---- History tab

const HistoryTab = ({ token, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/broker-dedup/merge-history?token=${token}`);
      setRows(res.data?.data?.history || []);
    } catch (e) {
      toast.error('Could not load history');
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const restore = async (id) => {
    if (!window.confirm('Restore this merge? Duplicate brokers will be marked active again.')) return;
    try {
      await axios.post(`${API}/broker-dedup/merge/${id}/restore?token=${token}`);
      toast.success('Merge restored');
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not restore');
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Recent merges</CardTitle>
        <CardDescription>
          Audit trail of dedup merges. Each is restorable within the retention window.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && !rows.length ? (
          <Skeleton className="h-24 w-full" />
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-slate-500">No merges yet.</div>
        ) : (
          rows.map((h) => (
            <div key={h.id} className="border border-slate-200 dark:border-slate-700 rounded-md p-3 text-sm">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">
                    {h.master_snapshot?.name || h.master_snapshot?.company || h.master_id}
                  </span>
                  <Badge variant="outline" className="text-[10px]">{h.status}</Badge>
                  <span className="text-xs text-slate-500">
                    by {h.actor_name} on {h.created_at && new Date(h.created_at).toLocaleString()}
                  </span>
                </div>
                {h.status === 'completed' && (
                  <Button size="sm" variant="outline" className="h-8" onClick={() => restore(h.id)}>
                    <Undo2 size={13} className="mr-1.5" /> Restore
                  </Button>
                )}
                {h.status === 'restored' && (
                  <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">
                    Restored
                  </Badge>
                )}
              </div>
              <div className="mt-1 text-xs text-slate-600">
                Merged {(h.duplicate_snapshots || []).length} duplicate(s) ·
                {h.agents_reassigned !== undefined ? ` ${h.agents_reassigned} agents` : ''}
                {h.sales_reassigned !== undefined ? ` · ${h.sales_reassigned} sales` : ''}
              </div>
              {h.reason && <p className="text-xs italic text-slate-500 mt-1">"{h.reason}"</p>}
              {h.error && <p className="text-xs text-red-600 mt-1">Error: {h.error}</p>}
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
};

// ---- Settings tab

const SettingsTab = ({ token }) => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/broker-dedup/config?token=${token}`);
      setCfg(res.data?.data);
    } catch (e) {
      toast.error('Could not load config');
    }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/broker-dedup/config?token=${token}`, cfg);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  if (!cfg) return <Skeleton className="h-48 w-full" />;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Dedup settings</CardTitle>
        <CardDescription>
          Tune the fuzzy thresholds, conflict rule, and retention. Changes apply tenant-wide immediately.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Fuzzy match threshold (%)</Label>
            <Input
              type="number"
              min={50} max={100}
              value={cfg.fuzzy_threshold}
              onChange={(e) => setCfg({ ...cfg, fuzzy_threshold: parseInt(e.target.value, 10) })}
            />
            <p className="text-xs text-slate-500 mt-1">
              Minimum similarity to surface a warning. Default 85.
            </p>
          </div>
          <div>
            <Label className="text-xs">Auto-merge threshold (%)</Label>
            <Input
              type="number"
              min={80} max={100}
              value={cfg.auto_merge_threshold}
              onChange={(e) => setCfg({ ...cfg, auto_merge_threshold: parseInt(e.target.value, 10) })}
            />
            <p className="text-xs text-slate-500 mt-1">
              Plus RERA match → safe to auto-merge with manager approval. Default 95.
            </p>
          </div>
          <div>
            <Label className="text-xs">Client lookback (days)</Label>
            <Input
              type="number"
              min={1} max={3650}
              value={cfg.lookback_days}
              onChange={(e) => setCfg({ ...cfg, lookback_days: parseInt(e.target.value, 10) })}
            />
            <p className="text-xs text-slate-500 mt-1">
              How far back to scan for cross-broker duplicate clients. Default 90.
            </p>
          </div>
          <div>
            <Label className="text-xs">Soft-delete retention (days)</Label>
            <Input
              type="number"
              min={1} max={365}
              value={cfg.soft_delete_days}
              onChange={(e) => setCfg({ ...cfg, soft_delete_days: parseInt(e.target.value, 10) })}
            />
            <p className="text-xs text-slate-500 mt-1">
              How long merged duplicates can be restored. Default 30.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Conflict resolution rule</Label>
            <select
              className="w-full border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-slate-900"
              value={cfg.conflict_rule || 'manual'}
              onChange={(e) => setCfg({ ...cfg, conflict_rule: e.target.value })}
            >
              <option value="manual">Manual review every time</option>
              <option value="first_to_register">First-to-register wins (auto)</option>
              <option value="lock_14_days">Lock to first broker for 14 days</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">
              The spec says this comes from Indirect Sales leadership — change once they decide.
            </p>
          </div>
          <div>
            <Label className="text-xs">Override requires manager approval</Label>
            <select
              className="w-full border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1.5 text-sm bg-white dark:bg-slate-900"
              value={String(cfg.require_manager_override !== false)}
              onChange={(e) => setCfg({ ...cfg, require_manager_override: e.target.value === 'true' })}
            >
              <option value="true">Yes (admin / HOD only can merge)</option>
              <option value="false">No (anyone with broker write access)</option>
            </select>
          </div>
        </div>

        <div>
          <Label className="text-xs">Client match fields</Label>
          <div className="flex flex-wrap gap-3 mt-1">
            {['phone', 'email', 'emirates_id'].map((f) => (
              <label key={f} className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={(cfg.client_match_fields || []).includes(f)}
                  onChange={(e) => {
                    const cur = new Set(cfg.client_match_fields || []);
                    if (e.target.checked) cur.add(f); else cur.delete(f);
                    setCfg({ ...cfg, client_match_fields: Array.from(cur) });
                  }}
                />
                {f.replace('_', ' ')}
              </label>
            ))}
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving} className="bg-violet-600 hover:bg-violet-500 text-white">
            {saving ? <><RefreshCw size={13} className="mr-1.5 animate-spin" /> Saving…</> : 'Save settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
